import React, { useState, useEffect } from "react";
import _ from 'lodash';
import {
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Chip,
} from "@mui/material";
import "./style.scss";
import { removeSpecialCharAndNumbers } from "../../../utils";
import SearchInputComponent from "../search-input";
import PropTypes from "prop-types";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MIN_LENGTH_API_SEARCH = 3;
const MIN_LENGTH_LOCAL_SEARCH = 1;

// sample way to use custom dropdown
// let uniqueKey = "value",textKey = "text",PLACEHOLDER = 'select value';
//   let map = {
//     "1": { [uniqueKey]: "1", [textKey]: "one" },
//     "2": { [uniqueKey]: "2", [textKey]:"two"},
//   }
//   const [value, setValue] = useState<string>('');

function CustomDropdown(props) {
  const {
    isMultiSelect = false,
    optionsMap = {},
    placeholder,
    textBoxLabel,
    parentClass = "w-[100%]",
    getListItemText,
    isMandatory = false,
    dropdownHeading,
    searchPlaceholder,
    preSelectedValues, // default options selected. if maxSelectionAllowed is enabled, extra options will automatically be removed from last
    onSubmit,
    uniqueKey = "mid",
    componentId,
    onLoadMoreClick,
    isShowLoadMore,
    onSearchApi,
    errorMessage = "",
    isDisabled = false,
    showErrorBox = true,
    isDisableOption,
    enableSelectAll = false,
    addEmptyOption = false,
    showCountType = CustomDropdownCountType.none,
    countSuffix = "branches",
    selectedTextClass = "",
    showSearchInput = false,
    getSelectedItemText,
    regexCheck = removeSpecialCharAndNumbers,
    dropdownSelectClass = "",
    labelClassName = "",
    multiselectOptions = {
      maxSelectionAllowed: 0, // limits the no of options that can be selected. 0 means no limit is there.
      showSelectedOptions: true,
      showAllSelected: false,
    },
    customFormClasses = {},
    isSelectOpen: isSelectOpenProp,
    setIsSelectOpen: setIsSelectOpenProp,
  } = props;
  const { maxSelectionAllowed = 0, showSelectedOptions = true, showAllSelected = false } =
    multiselectOptions;
  const [optionsList, setOptionsList] = useState([]);
  const [selected, setSelected] = useState([]);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [searchData, setSearchData] = useState({
    value: "",
    results: [],
  });

  const isSelectControlled = isSelectOpenProp !== undefined; // tells if the select component is controlled by the parent or local state
  const minSearchLength = onSearchApi ? MIN_LENGTH_API_SEARCH: MIN_LENGTH_LOCAL_SEARCH;
  const isAllSelected =
    optionsList.length > 0 && selected.length === optionsList.length;
    const MenuProps = {
      PaperProps: {
        style: {
          maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
          width: 250,
        },
      },
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "center",
      },
      transformOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      variant: "menu",
    };

  useEffect(() => {
    if (Object.keys(optionsMap).length)
      setOptionsList(Object.values(optionsMap));
  }, [optionsMap]);

  useEffect(() => {
    let selectedValue = [];
    if (preSelectedValues?.length) {
      selectedValue = isMultiSelect ? preSelectedValues : [preSelectedValues];
    }
    if (
      maxSelectionAllowed > 0 &&
      selectedValue?.length > maxSelectionAllowed
    ) {
      selectedValue = selectedValue.slice(
        0,
        maxSelectionAllowed
      );
      onSubmit(selectedValue);
    } else {
      setSelected(selectedValue);
    }
  }, [preSelectedValues]);

  const handleChange = (event) => {
    let value = event.target.value;
    // if (value[value.length - 1] === "all") {
    //   setSelected(selected.length === optionsList.length ? [] : optionsList);
    //   return;
    // }
    const selectedOptions = isMultiSelect ? value : [value];
    // setSelected(selectedOptions);
    if (
      maxSelectionAllowed === 0 ||
      selectedOptions?.length <= maxSelectionAllowed
    ) {
      onSubmit(selectedOptions);
    }
  };

  /**
   * On search input it will parse the menuItemsMap and it will filter out all the itemIDs based on the search string on longname of the item
   * and if the searchAPi prop is provided then it call the parent to give the search results
   * @param searchStr
   */
  const onSerachInput = (searchStr) => {
    const results = [];
    if (searchStr) {
      if (onSearchApi) {
        const callback = (response) => {
          results.push(...(response?.results || []));
          setSearchData({ value: searchStr, results });
        };
        onSearchApi(searchStr, callback);
      } else {
        setSearchData({ value: searchStr, results });
        for (var i = 0, len = optionsList.length; i < len; i++) {
          if (
            getListItemText(optionsList[i])
              ?.toLowerCase()
              ?.includes(searchStr?.toLowerCase())
          ) {
            results.push(optionsList[i]);
          }
        }
      }
    }
    setSearchData({ value: searchStr, results });
  };

  function renderSelectAll() {
    return (
      <MenuItem
        value="all"
        className="border-b-[1px] border-[#F0F0F0]"
        disabled
        // onKeyDown={(e) => e.stopPropagation()}
        // onClick={(e) => e.stopPropagation()}
      >
        <ListItemIcon>
          <Checkbox
            checked={isAllSelected}
            indeterminate={
              selected.length > 0 && selected.length < optionsList.length
            }
          />
        </ListItemIcon>
        <ListItemText primary="All Selected" />
      </MenuItem>
    );
  }

  function renderOptionsList(arrToIterate) {
    const maxLimitReached =
      maxSelectionAllowed > 0 && selected?.length >= maxSelectionAllowed;
    return (
      !!arrToIterate.length &&
      arrToIterate.map((option, index) => {
        const isSelected = selected?.indexOf(_.get(option, uniqueKey)) > -1;
        return (
          <MenuItem
            key={index}
            disabled={
              isDisableOption?.(_.get(option, uniqueKey)) ||
              (maxLimitReached && !isSelected)
            }
            value={_.get(option, uniqueKey)}
            className={`${!isMultiSelect && isSelected ? "active" : ""} 
            `}
          >
            {isMultiSelect && (
              <ListItemIcon>
                <Checkbox checked={isSelected} />
              </ListItemIcon>
            )}
            <ListItemText primary={getListItemText(option)} />
          </MenuItem>
        );
      })
    );
  }

  function renderSearchInput() {
    return (
      <Box
        className="search-menu-item border-b-[1px] p-16 border-[#F0F0F0] opacity-100"
        onKeyDown={(e) => e.stopPropagation()}
      >
        <SearchInputComponent
          placeholder={searchPlaceholder || "Search..."}
          customClass={"text-15 py-12"}
          onSerachInput={onSerachInput}
          searchMinLength={minSearchLength}
          regexCheck={regexCheck}
          elementWidthClass={"w-[100%]"}
          onClick={(e) => e.stopPropagation()}
        ></SearchInputComponent>
      </Box>
    );
  }

  function renderSelectedItems() {
    return (
      !!selected.length &&
      selected.map((option, index) => (
        <MenuItem key={index} value={option}>
          {isMultiSelect && (
            <ListItemIcon>
              <Checkbox checked={true} />
            </ListItemIcon>
          )}
          <ListItemText
            primary={getListItemText(optionsMap[option?.toLowerCase()])}
          />
        </MenuItem>
      ))
    );
  }

  function displayOptions() {
    if (searchData?.value?.length >= minSearchLength) {
      if (searchData?.results?.length) {
        return renderOptionsList(searchData.results);
      } else {
        return (
          <div className="flex flex-col w-full items-center justify-center py-40 px-20">
            <p className="text-20 font-semibold opacity-40 mb-6">
              No results found
            </p>
            <p className="text-13 font-normal opacity-40">
              Please try using other name or keyword
            </p>
          </div>
        );
      }
    }
    return renderOptionsList(optionsList);
  }

  function renderDisabledHeading() {
    let headingText = "";
    if (searchData.value.length >= minSearchLength && !!searchData.results.length)
      headingText += `${searchData.results.length} results found`;
    else if (dropdownHeading) headingText = dropdownHeading;

    return (
      !!headingText.length && (
        <MenuItem
          disabled
          value=""
          className="border-b-[1px] border-[#F0F0F0] opacity-100"
        >
          <p className="text-14 font-semibold text-[#9C9C9C]">{headingText}</p>
        </MenuItem>
      )
    );
  }

  function renderEmptyOption() {
    return (
      <MenuItem
        value=""
        className="border-b-[1px] border-[#F0F0F0] opacity-100 flex justify-between items-center"
      >
        <p className="text-14 font-medium text-[#9C9C9C] italic">
          {placeholder || "Choose an Option"}
        </p>
      </MenuItem>
    );
  }

  function isShowSelectedItems() {
    return (
      showSelectedOptions &&
      isMultiSelect &&
      !!selected.length &&
      searchData.value.length < 3
    );
  }

  function renderDataWithCount(item) {
    if (showCountType === CustomDropdownCountType.exact) {
      return <div className={`flex ${selectedTextClass}`}>{item?.length} {countSuffix} selected</div>
    }
    //handle lower case and no case change
    const data = _.get(optionsMap, item[0]) || _.get(optionsMap, item[0]?.toLowerCase())
    return item?.length > 0 ? (
      <div className={`flex ${selectedTextClass}`}>
        <div className="text-[14px]">{getListItemText(data)}</div>
        {item.length > 1 && (
          <div className="ml-5 text-[14px]">
            + {item.length - 1} {countSuffix || ""}
          </div>
        )}
      </div>
    ) : (
      <></>
    );
  }

  function renderSelectionInfo() {
    return (
      <div className="w-full bg-black/5">
        <div className="text-[12px] text-black/50 px-16 pt-12">
          You can only select {maxSelectionAllowed} options at a time
        </div>
        <div className="text-15 flex items-center justify-between px-16 py-12">
          <span className="text-black/70 font-medium">Options Selected</span>
          <span className="text-black font-semibold">
            {selected?.length || 0}/{maxSelectionAllowed}
          </span>
        </div>
      </div>
    );
  }

  return (
    <section
      className={`custom-dropdown rounded-[8px] ${parentClass} ${
        isDisabled && "pointer-events-none"
      }`}
      key={componentId}
    > {textBoxLabel ? (
        <p className="mb-8 text-14 capitalize text-[#656565]">
          {textBoxLabel}
          {isMandatory && <span className="text-[#9A3131]"> *</span>}
        </p>
      ) : null}
      <FormControl classes={customFormClasses}>
        <InputLabel id="mutiple-select-label" className={labelClassName}>{placeholder}</InputLabel>
        <Select
          open={isSelectControlled ? isSelectOpenProp : isSelectOpen}
          onClose={() => isSelectControlled ? setIsSelectOpenProp(false) : setIsSelectOpen(false)}
          onOpen={() => isSelectControlled ? setIsSelectOpenProp(true) : setIsSelectOpen(true)}
          className={`${errorMessage ? "mui-select" : dropdownSelectClass} ${isDisabled ? 'bg-ED' : ""}`}
          defaultValue=""
          labelId="mutiple-select-label"
          placeholder={searchPlaceholder || "Search..."}
          multiple={isMultiSelect}
          value={selected}
          onChange={handleChange}
          renderValue={(item) => {
            return isMultiSelect ? (

              <Box sx={{ display: "flex", flexWrap: showAllSelected ? "wrap": "nowrap", overflow: "hidden", gap: 0.75 }}>
                { showCountType === CustomDropdownCountType.none
                  ? item.map((value) => (
                      <Chip
                        key={getListItemText(optionsMap[value])}
                        label={getListItemText(
                          optionsMap[value?.toLowerCase()]
                        )}
                        className={`rounded-[6px] ${selectedTextClass} `}
                      />
                    ))
                  : renderDataWithCount(item)}
              </Box>
            ) : getSelectedItemText ? (
              getSelectedItemText(optionsMap[item[0]?.toLowerCase()])
            ) : (
              getListItemText(optionsMap[item[0]?.toLowerCase()])
            );
          }}
          MenuProps={MenuProps}
        >
          {enableSelectAll && maxSelectionAllowed === 0 && renderSelectAll()}
          {maxSelectionAllowed > 0 && maxSelectionAllowed < optionsList?.length && renderSelectionInfo()}
          {(showSearchInput || optionsList.length > 10) && renderSearchInput()}
          {isShowSelectedItems() && (
            <MenuItem
              disabled
              value=""
              className="border-b-[1px] border-[#F0F0F0] opacity-100"
            >
              <p className="text-14 font-medium text-[#9C9C9C]">Selected</p>
            </MenuItem>
          )}
          {isShowSelectedItems() && renderSelectedItems()}
          {renderDisabledHeading()}
          {!isMultiSelect &&
            addEmptyOption &&
            searchData.value.length < 3 &&
            renderEmptyOption()}
          {displayOptions()}
          {isShowLoadMore && (
            <MenuItem
              value=""
              className="border-b-[1px] border-[#F0F0F0] opacity-100 flex justify-center cursor-pointer"
              onClick={onLoadMoreClick}
            >
              <p className={`text-14 font-medium text-blue0074FB`}>
                Load more...
              </p>
            </MenuItem>
          )}
        </Select>
      </FormControl>
      {showErrorBox && (
        <p className="mt-4 text-12 font-normal text-[#9A3131] h-[18px]">
          {errorMessage ? errorMessage : ""}
        </p>
      )}
    </section>
  );
}

export default CustomDropdown;

export const CustomDropdownCountType = {
  onePlus: "onePlus",
  none: "none",
  exact: 'exact'
};

CustomDropdown.propTypes = {
  searchPlaceholder: PropTypes.string,
  placeholder: PropTypes.string,
  dropdownHeading: PropTypes.string,
  textBoxLabel: PropTypes.string,
  parentClass: PropTypes.string,
  isMultiSelect: PropTypes.bool,
  optionsMap: PropTypes.object,
  preSelectedValues: PropTypes.any,
  onSubmit: PropTypes.func,
  getListItemText: PropTypes.func,
  uniqueKey: PropTypes.string,
  componentId: PropTypes.string,
  onSearchApi: PropTypes.func,
  isShowLoadMore: PropTypes.bool,
  onLoadMoreClick: PropTypes.func,
  isMandatory: PropTypes.bool,
  errorMessage: PropTypes.string,
  isDisabled: PropTypes.bool,
  enableSelectAll: PropTypes.bool,
  showErrorBox: PropTypes.bool, //it will be a boolean and if it's false then it will not take a space of error div
  isDisableOption: PropTypes.func,
  addEmptyOption: PropTypes.bool,
  showCountType : PropTypes.oneOf(Object.values(CustomDropdownCountType)),
  countSuffix : PropTypes.string,
  selectedTextClass : PropTypes.string,
  showSearchInput: PropTypes.bool,
  getSelectedItemText: PropTypes.func,
  regexCheck: PropTypes.func,
  dropdownSelectClass: PropTypes.string,
  multiselectOptions: PropTypes.shape({
    maxSelectionAllowed: PropTypes.number,
    showSelectedOptions: PropTypes.bool,
    showAllSelected: PropTypes.bool
  }),
  labelClassName: PropTypes.string,
  customFormClasses: PropTypes.object,
  isSelectOpen: PropTypes.bool,
  setIsSelectOpen: PropTypes.func,
};
